import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const sliderData = [
    {
        id: 1,
        question: "What degrees or training do I need to work at Mobikode?",
        answer: "We believe great talent comes from different backgrounds and experiences. That’s why we look beyond degrees and focus on your skills, achievements, and potential. Every role is unique, and the specific requirements for each position are clearly outlined in the job description."
    },
    {
        id: 2,
        question: "I am a former employee. Am I eligible to be rehired?",
        answer: "Absolutely! Former employees are always welcome to apply again. We value the experience you've gained since your time with us and would be excited to explore opportunities that match your current skills, interests, and career goals."
    },
    {
        id: 3,
        question: "Where can I explore leadership and executive-Level roles at Mobikode?",
        answer: "Ready to take the next step in your leadership journey? Explore senior and executive opportunities on our careers portal and find a role where your experience, vision, and leadership can make a real difference."
    },
    {
        id: 4,
        question: "How does the interview process work? ",
        answer: "Our interview process is simple, transparent, and focused on getting to know you better. It usually includes an initial conversation, a role-specific assessment, and friendly discussions with our team to understand your skills, experience, and how you’ll thrive within our culture."
    }
];

const FindingRightFitSection = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
        breakpoints: {
            "(min-width: 640px)": {
                slides: { perView: 1.5, spacing: 20 },
            },
            "(min-width: 768px)": {
                slides: { perView: 2.2, spacing: 20 },
            },
            "(min-width: 1024px)": {
                slides: { perView: 3, spacing: 24 },
            },
        },
        slides: { perView: 1.1, spacing: 16 },
    });

    const fadeUpVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const isNextDisabled = () => {
        if (!instanceRef.current) return true;
        const totalSlides = instanceRef.current.track.details.slides.length;
        const perView = instanceRef.current.options.slides.perView;
        return currentSlide >= Math.ceil(totalSlides - perView);
    };

    return (
        <section className="w-full pb-[40px] md:pb-[100px] bg-white overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-[20px] md:px-[40px] lg:px-[80px]">

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUpVariant}
                    className="flex justify-center mb-[24px] md:mb-[60px]"
                >
                    <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] tracking-tight">
                        Finding the right fit
                    </h2>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={fadeUpVariant}
                    className="relative"
                >
                    <div ref={sliderRef} className="keen-slider !overflow-visible lg:!overflow-hidden">
                        {sliderData.map((slide) => (
                            <div className="keen-slider__slide" key={slide.id}>
                                <div className="group relative flex flex-col p-[24px] sm:p-[30px] md:p-[40px] bg-[#F0F0F0] md:hover:bg-[#01386A] transition-colors duration-500 h-[320px] sm:h-[360px] md:h-[400px] overflow-hidden cursor-pointer rounded-sm">

                                    <h3 className="font-sora text-[16x] md:text-[20px] font-[600] text-[#01386A] md:group-hover:text-white transition-colors duration-300 mb-[16px] md:mb-[20px]">
                                        {slide.question}
                                    </h3>

                                    <div className="grid grid-rows-[1fr] md:grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-in-out group-hover:md:grid-rows-[1fr]">
                                        <div className="overflow-hidden">
                                            <p className="font-graphik text-[14px] md:text-[16px] font-[500] text-[#49577A] md:text-white leading-[1.5] md:leading-[1.6] opacity-100 md:opacity-0 group-hover:md:opacity-100 transition-opacity duration-500 delay-100 pb-[10px]">
                                                {slide.answer}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="absolute bottom-[24px] sm:bottom-[30px] md:bottom-[40px] left-[24px] sm:left-[30px] md:left-[40px] transition-opacity duration-300 md:group-hover:opacity-0 flex items-center mt-auto">
                                        <span className="font-graphik text-[14px] md:text-[16px] font-[500] text-[#01386A] border-b border-[#01386A] pb-[2px]">
                                            Read More
                                        </span>
                                        <span className="text-[#ED6800] ml-2 font-bold text-[16px]">&gt;</span>
                                    </div>

                                </div>
                            </div>
                        ))}
                    </div>

                    {loaded && instanceRef.current && (
                        <div className="flex items-center gap-[16px] mt-[24px] md:mt-[40px]">
                            <button
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.prev()}
                                disabled={currentSlide === 0}
                                className={`w-[40px] h-[40px] flex items-center justify-center rounded-full border border-[#ED6800] text-[#ED6800] transition-colors ${currentSlide === 0 ? "opacity-30 cursor-not-allowed border-gray-300 text-gray-300" : "hover:bg-[#ED6800] hover:text-white"}`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={(e) => e.stopPropagation() || instanceRef.current?.next()}
                                disabled={isNextDisabled()}
                                className={`w-[40px] h-[40px] flex items-center justify-center rounded-full border border-[#ED6800] text-[#ED6800] transition-colors ${isNextDisabled() ? "opacity-30 cursor-not-allowed border-gray-300 text-gray-300" : "hover:bg-[#ED6800] hover:text-white"}`}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </motion.div>

            </div>
        </section>
    );
};

export default FindingRightFitSection;